import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type VehicleFormSchema, vehicleFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { VehicleForm } from "@/forms/vehicle-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditVehicleModalProps extends ModalProps {
	vehicleId: string;
	onComplete?: () => Promise<void>;
}

export function EditVehicleModal({
	isOpen,
	onClose,
	vehicleId,
	onComplete,
}: EditVehicleModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Form */
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.vehicles[":vehicleId"].$get({
			param: { vehicleId },
		});
		const data = await responseHandler(response);
		if (!data.vehicleForm) throw new Error("Failed to load vehicle data");
		return data.vehicleForm;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(vehicleFormSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: VehicleFormSchema) {
		try {
			const response = await client.vehicles[":vehicleId"].$patch({
				param: { vehicleId },
				json,
			});
			// An update that reached no row answers a refusal; the modal stays open with the message
			// rather than closing on a success that changed nothing.
			await responseHandler(response);
			await onComplete?.();
			onClose();
			methods.reset();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnReject(errors: typeof methods.formState.errors) {
		toast.error(JSON.stringify(errors));
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("vehicles.edit_vehicle")}
				onSubmit={methods.handleSubmit(handleOnSubmit, handleOnReject)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<VehicleForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
